export const ONEBITMAP_SHARED_CONFIG_TOOL_SLUG = 'converter-para-bitmap';

export const ONEBITMAP_MATERIAL_IDS = [
  'dtf-textil',
  'dtf-uv',
  'sublimacao',
  'adesivo-uv',
] as const;

export type OneBitmapMaterialId = (typeof ONEBITMAP_MATERIAL_IDS)[number];
export type OneBitmapColorMode = 'CMYK' | 'RGB';
export type OneBitmapBackground = 'transparent' | 'solid';

export interface OneBitmapMaterialConfig {
  id: OneBitmapMaterialId;
  label: string;
  dpi: number;
  sortOrder: number;
  active: boolean;
}

export interface OneBitmapSharedConfig {
  materials: OneBitmapMaterialConfig[];
  defaults: {
    colorMode: OneBitmapColorMode;
    background: OneBitmapBackground;
  };
}

export interface ParsedOneBitmapSharedConfig {
  config: OneBitmapSharedConfig;
  issues: string[];
  usedFallback: boolean;
}

const DEFAULT_ONEBITMAP_MATERIALS: OneBitmapMaterialConfig[] = [
  {
    id: 'dtf-textil',
    label: 'DTF TÊXTIL',
    dpi: 300,
    sortOrder: 1,
    active: true,
  },
  {
    id: 'dtf-uv',
    label: 'DTF UV',
    dpi: 400,
    sortOrder: 2,
    active: true,
  },
  {
    id: 'sublimacao',
    label: 'SUBLIMAÇÃO',
    dpi: 250,
    sortOrder: 3,
    active: true,
  },
  {
    id: 'adesivo-uv',
    label: 'ADESIVO UV',
    dpi: 300,
    sortOrder: 4,
    active: true,
  },
];

export const DEFAULT_ONEBITMAP_SHARED_CONFIG: OneBitmapSharedConfig = {
  materials: DEFAULT_ONEBITMAP_MATERIALS,
  defaults: {
    colorMode: 'CMYK',
    background: 'transparent',
  },
};

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isMaterialId(value: unknown): value is OneBitmapMaterialId {
  return typeof value === 'string' && ONEBITMAP_MATERIAL_IDS.includes(value as OneBitmapMaterialId);
}

function isColorMode(value: unknown): value is OneBitmapColorMode {
  return value === 'CMYK' || value === 'RGB';
}

function isBackground(value: unknown): value is OneBitmapBackground {
  return value === 'transparent' || value === 'solid';
}

function normalizeMaterial(
  input: unknown,
  fallback: OneBitmapMaterialConfig,
  issues: string[],
): OneBitmapMaterialConfig {
  if (!isObjectRecord(input)) {
    issues.push(`Material ${fallback.id} veio em formato inválido.`);
    return { ...fallback };
  }

  const label =
    typeof input.label === 'string' && input.label.trim() !== ''
      ? input.label.trim()
      : fallback.label;

  const dpi = Number(input.dpi);
  const normalizedDpi =
    Number.isFinite(dpi) && dpi >= 72 && dpi <= 1200
      ? Math.round(dpi)
      : fallback.dpi;
  if (normalizedDpi === fallback.dpi && dpi !== fallback.dpi) {
    issues.push(`Material ${fallback.id} recebeu DPI inválido e usou fallback.`);
  }

  const sortOrder = Number(input.sortOrder);
  const normalizedSortOrder =
    Number.isFinite(sortOrder) && sortOrder >= 0 && sortOrder <= 999
      ? Math.round(sortOrder)
      : fallback.sortOrder;

  return {
    id: fallback.id,
    label,
    dpi: normalizedDpi,
    sortOrder: normalizedSortOrder,
    active: typeof input.active === 'boolean' ? input.active : fallback.active,
  };
}

export function cloneOneBitmapSharedConfig(
  config: OneBitmapSharedConfig,
): OneBitmapSharedConfig {
  return {
    materials: config.materials.map((material) => ({ ...material })),
    defaults: { ...config.defaults },
  };
}

export function parseOneBitmapSharedConfig(input: unknown): ParsedOneBitmapSharedConfig {
  const fallback = cloneOneBitmapSharedConfig(DEFAULT_ONEBITMAP_SHARED_CONFIG);
  const issues: string[] = [];

  if (!isObjectRecord(input)) {
    return {
      config: fallback,
      issues: ['Configuração compartilhada ausente ou inválida.'],
      usedFallback: true,
    };
  }

  const inputMaterials = Array.isArray(input.materials) ? input.materials : [];
  const materialMap = new Map<OneBitmapMaterialId, OneBitmapMaterialConfig>();

  for (const fallbackMaterial of DEFAULT_ONEBITMAP_MATERIALS) {
    const remoteMaterial = inputMaterials.find((candidate) =>
      isObjectRecord(candidate) && isMaterialId(candidate.id) && candidate.id === fallbackMaterial.id,
    );

    materialMap.set(
      fallbackMaterial.id,
      normalizeMaterial(remoteMaterial, fallbackMaterial, issues),
    );
  }

  const defaultsInput = isObjectRecord(input.defaults) ? input.defaults : {};

  const colorMode = isColorMode(defaultsInput.colorMode)
    ? defaultsInput.colorMode
    : fallback.defaults.colorMode;
  if (!isColorMode(defaultsInput.colorMode) && defaultsInput.colorMode !== undefined) {
    issues.push('colorMode inválido na configuração compartilhada.');
  }

  const background = isBackground(defaultsInput.background)
    ? defaultsInput.background
    : fallback.defaults.background;
  if (!isBackground(defaultsInput.background) && defaultsInput.background !== undefined) {
    issues.push('background inválido na configuração compartilhada.');
  }

  const materials = Array.from(materialMap.values()).sort(
    (left, right) => left.sortOrder - right.sortOrder,
  );

  return {
    config: {
      materials,
      defaults: {
        colorMode,
        background,
      },
    },
    issues,
    usedFallback: issues.length > 0,
  };
}

export function serializeOneBitmapSharedConfig(config: OneBitmapSharedConfig): string {
  return JSON.stringify({
    materials: [...config.materials]
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .map((material) => ({
        id: material.id,
        label: material.label.trim(),
        dpi: Math.round(material.dpi),
        sortOrder: Math.round(material.sortOrder),
        active: Boolean(material.active),
      })),
    defaults: {
      colorMode: config.defaults.colorMode,
      background: config.defaults.background,
    },
  });
}

export function getOneBitmapRuntimeSharedConfigEndpoint(): string {
  return `/tools/${ONEBITMAP_SHARED_CONFIG_TOOL_SLUG}/shared-config`;
}

export function getOneBitmapAdminSharedConfigEndpoint(): string {
  return `/admin/tools/${ONEBITMAP_SHARED_CONFIG_TOOL_SLUG}/shared-config`;
}
